<template>
  <Teleport to="body">
    <div v-if="visible" class="language-modal-overlay" @click.self="$emit('close')">
      <div class="language-modal">
        <button class="language-modal__close" type="button" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12"
stroke="currentColor"
stroke-width="2"
stroke-linecap="round"/>
          </svg>
        </button>

        <h2 class="language-modal__title">{{ t('profile.selectLanguage') }}</h2>

        <div class="language-modal__grid">
          <button
            v-for="lang in languages"
            :key="lang.code"
            :class="['language-item', { 'language-item--selected': currentLanguage === lang.code }]"
            @click="selectLanguage(lang.code)"
          >
            <span class="language-item__native">{{ lang.native }}</span>
            <span class="language-item__english">{{ t(`languages.${lang.code}`) }}</span>
            <svg
              v-if="currentLanguage === lang.code"
              class="language-item__check"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path d="M5 13l4 4L19 7"
stroke="currentColor"
stroke-width="2"
stroke-linecap="round"
stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select', code: string): void
}>()

const { locale, t } = useI18n({ useScope: 'global' })
const currentLanguage = computed(() => locale.value)

interface LanguageOption {
  code: string
  native: string
}

const languages: LanguageOption[] = [
  { code: 'en', native: 'English' },
  { code: 'ar', native: 'العربية' },
  { code: 'az', native: 'Azərbaycan' },
  { code: 'bn', native: 'বাংলা' },
  { code: 'cn', native: '中国人' },
  { code: 'cz', native: 'Čeština' },
  { code: 'de', native: 'Deutsch' },
  { code: 'es', native: 'Español' },
  { code: 'fr', native: 'Français' },
  { code: 'hi', native: 'हिंदी' },
  { code: 'id', native: 'Bahasa Indonesia' },
  { code: 'it', native: 'Italiano' },
  { code: 'iw', native: 'עברית' },
  { code: 'ja', native: '日本語' },
  { code: 'ka', native: 'ქართული' },
  { code: 'kk', native: 'Қазақ' },
  { code: 'ko', native: '한국인' },
  { code: 'ky', native: 'Кыргызча' },
  { code: 'pl', native: 'Polski' },
  { code: 'pt', native: 'Portuguesa' },
  { code: 'ro', native: 'Română' },
  { code: 'ru', native: 'Русский' },
  { code: 'sw', native: 'Kiswahili' },
  { code: 'tr', native: 'Türkçe' },
  { code: 'uz', native: 'Oʻzbekcha' },
  { code: 'vi', native: 'Tiếng Việt' },
  { code: 'zh', native: '简体中文' },
]

function selectLanguage(code: string) {
  emit('select', code)
  emit('close')
}
</script>

<style scoped>
.language-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.language-modal {
  background: var(--hm-surface);
  border-radius: var(--ds-radius-modal);
  width: 100%;
  max-width: 780px;
  max-height: 85vh;
  padding: 32px;
  position: relative;
  box-shadow: var(--hm-shadow-soft);
  animation: modalSlideIn 0.2s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.language-modal__close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--hm-text-secondary);
  transition: color 0.15s;
}

.language-modal__close:hover {
  color: var(--hm-text-primary);
}

.language-modal__close svg {
  width: 20px;
  height: 20px;
}

.language-modal__title {
  font-size: var(--p-text-22);
  font-weight: var(--ds-weight-bold);
  color: var(--hm-text-primary);
  margin: 0 0 24px;
  text-align: center;
}

.language-modal__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.language-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: var(--ds-radius-card);
  border: 1px solid var(--hm-border);
  background: var(--hm-surface);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
}

.language-item:hover {
  border-color: var(--hm-accent);
  background: var(--hm-surface-alt);
}

.language-item--selected {
  border-color: var(--hm-accent);
  background: var(--ds-accent-soft-04);
}

.language-item__native {
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
}

.language-item__english {
  font-size: var(--p-text-13);
  color: var(--hm-text-secondary);
  flex: 1;
}

.language-item__check {
  width: 20px;
  height: 20px;
  color: var(--hm-accent);
  flex-shrink: 0;
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .language-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }
  
  .language-modal {
    max-width: 100%;
    max-height: 90vh;
    border-radius: var(--ds-radius-modal) var(--ds-radius-modal) 0 0;
    padding: 24px 16px 32px;
    overflow-y: auto;
  }
  
  .language-modal__grid {
    grid-template-columns: 1fr;
  }
}
</style>
